import { api } from '../../services/api';
import { isElectron } from '../../utils/platform';
import {
  deviceCommunicationService,
  DeviceOperation,
  DeviceOperations,
  CommunicationMode,
  DeviceOperationResult,
} from '../../services/deviceCommunication';
import { rawDeviceToHandle } from './mapping';
import { fetchPersonDeviceHandles } from './zones';
import type { DeviceHandle, DeviceDirection, SyncPersonPayload } from './types';

/**
 * DeviceIntegration — singleton façade for all device sync/remove operations.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │  Connection strategy                                                     │
 * │                                                                          │
 * │  Electron                                                                │
 * │    For each device the engine (DeviceCommunicationService) probes:       │
 * │      1. local_ip_address  → TCP probe 400 ms                             │
 * │      2. ip_address        → TCP probe 1500 ms                            │
 * │    Whichever responds first receives the actual operation (12 s limit).  │
 * │    If both fail → NO cloud fallback for sync/remove.                     │
 * │    The backend handles cloud delivery via triggerIoTRemoval /            │
 * │    deferred-removals (calling cloud directly causes 504 cascades).       │
 * │                                                                          │
 * │  Browser                                                                 │
 * │    HTTPS blocks HTTP to devices.                                         │
 * │    All operations route to the backend cloud API.                        │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Usage:
 *   DeviceIntegration.instance.syncPerson(customerId, personId, payload)
 *   DeviceIntegration.instance.removePerson(customerId, personId)
 *   DeviceIntegration.instance.execute(device, DeviceOperations.SYNC_PERSON, payload)
 */
export class DeviceIntegration {
  private static _instance: DeviceIntegration;

  private constructor() {}

  /** The application-wide singleton. Never call `new DeviceIntegration()`. */
  static get instance(): DeviceIntegration {
    return (DeviceIntegration._instance ??= new DeviceIntegration());
  }

  // ─── Vendor mapping ────────────────────────────────────────────────────────

  /** Map a raw API device object to a DeviceHandle. */
  toHandle(rawDevice: any, direction?: DeviceDirection): DeviceHandle {
    return rawDeviceToHandle(rawDevice, direction);
  }

  // ─── Device resolution ─────────────────────────────────────────────────────

  /** Fetch all devices a person currently has access to (via effective-access-zones). */
  getPersonDevices(customerId: string, personId: string): Promise<DeviceHandle[]> {
    return fetchPersonDeviceHandles(customerId, personId);
  }

  // ─── Low-level operation ───────────────────────────────────────────────────

  /**
   * Execute a single device operation.
   *
   * Connection order (Electron / HYBRID):
   *   local_ip_address → ip_address → [cloud disabled for sync/remove]
   *
   * Use this when you need full control over per-device payload (e.g. per-device
   * validUntil timing). For bulk operations prefer syncPersonToDevices /
   * removePersonFromDevices.
   */
  execute(
    device: DeviceHandle,
    operation: DeviceOperation,
    payload: any,
    mode: CommunicationMode = CommunicationMode.HYBRID,
  ): Promise<DeviceOperationResult> {
    return deviceCommunicationService.executeOperation(device, operation, payload, mode);
  }

  // ─── Sync ──────────────────────────────────────────────────────────────────

  /**
   * Sync a person to all their authorized devices.
   *
   * Electron: resolves effective-access-zones, then tries each device directly
   *   (local IP → public IP). No cloud fallback — backend handles it.
   * Browser: POST /customers/:id/people/:id/sync (cloud only).
   */
  async syncPerson(
    customerId: string,
    personId: string,
    payload: SyncPersonPayload,
  ): Promise<void> {
    if (!isElectron()) {
      await this._cloudSyncPerson(customerId, personId);
      return;
    }
    const devices = await fetchPersonDeviceHandles(customerId, personId);
    await this.syncPersonToDevices(devices, personId, payload);
  }

  /**
   * Sync a person to a caller-supplied device list.
   *
   * Use when you already have the device list (avoids a second zone-fetch) or when
   * you need to filter/re-order devices before syncing (e.g. skip entrada devices
   * when a visitor is already inside).
   *
   * Electron only — noop in browser (cloud path is caller's responsibility there).
   */
  async syncPersonToDevices(
    devices: DeviceHandle[],
    personId: string,
    payload: SyncPersonPayload,
  ): Promise<void> {
    if (!isElectron() || devices.length === 0) return;
    await Promise.allSettled(
      devices.map(d =>
        deviceCommunicationService.executeOperation(
          d,
          DeviceOperations.SYNC_PERSON,
          { ...payload, person_id: personId },
          CommunicationMode.HYBRID,
        ).catch(() => {}),
      ),
    );
  }

  // ─── Remove ────────────────────────────────────────────────────────────────

  /**
   * Remove a person from all their authorized devices.
   *
   * Electron: resolves effective-access-zones, then removes from each device directly
   *   (local IP → public IP). No cloud fallback — backend handles deferred removal.
   * Browser: noop — backend's revoke API handles device removal.
   */
  async removePerson(customerId: string, personId: string): Promise<void> {
    if (!isElectron()) return;
    const devices = await fetchPersonDeviceHandles(customerId, personId);
    await this.removePersonFromDevices(devices, personId);
  }

  /**
   * Remove a person from a caller-supplied device list.
   *
   * IMPORTANT: always pass the person UUID as `person_id`.
   * Never pre-compute vendor-specific IDs — DeviceCommunicationService resolves
   * ControlID numeric IDs internally via load-then-delete.
   *
   * Electron only — noop in browser.
   */
  async removePersonFromDevices(devices: DeviceHandle[], personId: string): Promise<void> {
    if (!isElectron() || devices.length === 0) return;
    await Promise.allSettled(
      devices.map(d =>
        deviceCommunicationService.executeOperation(
          d,
          DeviceOperations.REMOVE_PERSON,
          { person_id: personId },
          CommunicationMode.HYBRID,
        ).catch(() => {}),
      ),
    );
  }

  // ─── Vendor-specific helpers ──────────────────────────────────────────────

  /**
   * ControlID: ensure the device has a default access group and return its ID.
   * Used after a successful direct sync to assign the user to the group.
   */
  ensureControlIDDefaultAccess(device: DeviceHandle, timeout?: number) {
    return deviceCommunicationService.ensureControlIDDefaultAccess(device, timeout);
  }

  /**
   * ControlID: assign all users currently on the device to the given group.
   */
  assignAllDeviceUsersToGroup(device: DeviceHandle, groupId: number) {
    return deviceCommunicationService.assignAllDeviceUsersToGroup(device, groupId);
  }

  // ─── Cloud paths ───────────────────────────────────────────────────────────
  //
  // Browser mode: ALL sync/remove routes through the cloud API.
  // Electron mode: sync/remove intentionally skip the cloud path.
  //   - Backend's triggerIoTRemoval goroutine handles deferred removal.
  //   - Direct cloud calls from Electron → 504 cascades when devices are offline.

  /** POST /sync — backend pushes to all authorized devices (browser-only path). */
  private async _cloudSyncPerson(customerId: string, personId: string): Promise<void> {
    await api.post(`/customers/${customerId}/people/${personId}/sync`);
  }

  /**
   * Cloud remove — intentionally unused in Electron.
   *
   * Backend handles removal via triggerIoTRemoval / deferred-removals.
   * Re-enable only if the deferred flow is replaced with synchronous cloud removal.
   */
  // private async _cloudRemovePerson(customerId: string, personId: string): Promise<void> {
  //   await api.delete(`/customers/${customerId}/people/${personId}/remove-from-devices`);
  // }
}
